import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../AuthContext';
import { BarChart3, Search } from 'lucide-react';

const MONTH_NAMES = [
  '', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const Reporting = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    end_date: new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0],
    user_id: '',
    project_id: '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [projRes] = await Promise.all([
          api.get('/projects'),
        ]);
        setProjects(projRes.data);

        if (isAdmin) {
          const userRes = await api.get('/users');
          setUsers(userRes.data);
        }
      } catch (err) {
        console.error('Fehler beim Laden der Filterdaten:', err);
      }
    };
    fetchMeta();
  }, [isAdmin]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.project_id) params.append('project_id', filters.project_id);

      const res = await api.get(`/reports/monthly?${params}`);
      setData(res.data);
    } catch (err) {
      console.error('Fehler beim Laden des Reports:', err);
      setError('Fehler beim Laden der Auswertung.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchReport();
  };

  return (
    <div>
      <div className="page-header">
        <h2><BarChart3 size={22} style={{ marginRight: 8, verticalAlign: 'middle' }} />Auswertung</h2>
        <p>Monatliche Übersicht der Arbeitszeiten und Tags</p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <span>Filter</span>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="filters" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label>Von</label>
                <input type="date" className="form-control" value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Bis</label>
                <input type="date" className="form-control" value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
              </div>
              {isAdmin && (
                <div className="form-group">
                  <label>Mitarbeiter</label>
                  <select className="form-control" value={filters.user_id}
                    onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}>
                    <option value="">Eigene Daten</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Projekt</label>
                <select className="form-control" value={filters.project_id}
                  onChange={(e) => setFilters({ ...filters, project_id: e.target.value })}>
                  <option value="">Alle Projekte</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <Search size={16} />
              {loading ? 'Laden...' : 'Auswertung laden'}
            </button>
          </form>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {data && Object.keys(data).length === 0 && (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>
            Keine Daten für den gewählten Zeitraum gefunden.
          </div>
        </div>
      )}

      {data && Object.keys(data)
        .sort((a, b) => Number(a) - Number(b))
        .map((year) => (
          <div className="card" key={year} style={{ marginBottom: 24 }}>
            <div className="card-header">
              <span>{year}</span>
            </div>
            <div className="card-body" style={{ padding: 0, overflow: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Monat</th>
                    <th style={{ textAlign: 'right' }}>Stunden</th>
                    <th>Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {data[year].map((row) => (
                    <tr key={row.month}>
                      <td>{MONTH_NAMES[row.month]}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {row.hours.toFixed(2)}
                      </td>
                      <td>
                        {row.tags.length > 0
                          ? row.tags.join(', ')
                          : <span style={{ color: 'var(--text-light)' }}>&mdash;</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      }
    </div>
  );
};

export default Reporting;
